/* eslint-disable no-nested-ternary */
import React, {
  ReactElement,
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { PaymentIntentResult, SetupIntentResult } from '@stripe/stripe-js';
import { observer } from 'mobx-react-lite';
import { useForm, FormProvider } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { useMst } from '../../../models/stores/Root';
import FormErrorLabel from '../../../components/input/FormErrorLabel';
import ShippingMethodSection from '../../../components/framework/ShippingMethodSection';
import AuthenticatedContactSection from '../../../components/framework/AuthenticatedContactSection';
import AuthenticatedShippingAddressSection from '../../../components/framework/AuthenticatedShippingAddressSection';
import AuthenticatedBillingAddressSection from '../../../components/framework/AuthenticatedBillingAddressSection';
import AuthenticatedPaymentSection from '../../../components/framework/AuthenticatedPaymentSection';
import CustomFieldSection from '../../../components/framework/CustomFieldSection';
import CVVUpdateSectionWithFormContext from '../../../components/framework/CVVUpdateSectionWithFormContext';
import OrderComment from '../../../components/input/OrderComment';
import Button from '../../../components/input/Button';
import SectionTitle from '../../../components/content/SectionTitle';
import CustomMessage from '../../../components/content/CustomMessage';
import {
  CustomerBillingAddressContract,
  CustomerShippingAddressContract,
} from '../../../types/Customer';
import NoPaymentSummary from '../../../components/summary/NoPaymentSummary';
import { AddressContract, AddressFormData } from '../../../types/Address';
import { SelectOption } from '../../../types/common';
import AddressFormWithButtons from '../../../components/input/AddressFormWithButtons';
import {
  APIValues,
  DefaultValues,
  SiteLimitationKeys,
  SpreedlyValues,
  UIConstants,
} from '../../../utils/constants';
import { getCustomFieldsSchemaWithFormContext } from '../../../models/schemas/CustomFields';
import { cvvSchema } from '../../../models/schemas/Payment';
import SpreedlyCvvOnly from '../../../components/payment/SpreedlyCvvOnly';
import SpreedlyChallengeModal from '../../../components/content/SpreedlyChallengeModal';
import ApiValidationError from '../../../models/classes/ApiValidationError';
import ServiceError from '../../../models/classes/ServiceError';
import {
  CartPaymentProfileCardContract,
  CartPaymentProfileContract,
} from '../../../types/PaymentProfile';
import {
  SpreedlyPostOrderResponse,
  SpreedlyThreeDSStatusEvent,
} from '../../../types/Spreedly';
import SpreedlyService from '../../../services/SpreedlyService';
import StripeInvisibleLoader from '../../../components/payment/StripeInvisibleLoader';
import PaymentMethodSectionWithFormContext from '../../../components/framework/PaymentMethodSectionWithFormContext';
import {
  PayPalAcpHostedFieldsResponseData,
  PayPalAcpMethods,
  PayPalOnApproveData,
  PayPalOnClickData,
} from '../../../types/PayPal';
import UtilityService from '../../../services/UtilityService';
import {
  getCardBrandFromPaymentMethod,
  getCardExpDateFromPaymentMethod,
  getIntentFromResponse,
  getPaymentIntentIdFromResponse,
  getPaymentMethodIdFromIntent,
  getPaymentMethodObject,
  getSetupIntentIdFromResponse,
} from '../../../utils/stripe/intentParsing';
import GiftCardSection from '../../../components/framework/GiftCardSection';
import AlertModal from '../../../components/content/AlertModal';

export type OneClickViewProps = {
  onLogout: () => void;
  onEditShipTo: () => void;
  onEditBillTo: () => void;
  onEditPayment: () => void;
  onScrollTopOfPage: () => void;
};

export type OneClickFormData = {
  cvv?: string;
  customFields?: {
    [x: string]: string;
  };
  paymentMethodId: string;
  creditCard: {
    cardNumber: string;
    cardHolderName: string;
    expMonth: string;
    expYear: string;
    cvv: string;
  };
  purchaseOrder?: {
    poNumber: string;
  };
  billingAddressMethodId: string;
  billingAddress: AddressContract;
  stripe?: string;
};

// Use Luhn's algorithm to check for a valid credit card
export const luhnCheck = (cardNumber: string) => {
  // Remove all non-digit characters
  const cleanedCardNumber = cardNumber.replace(/\D/g, '');
  let sum = 0;
  let shouldDouble = false;

  // Loop through the card number in reverse
  for (let i = cleanedCardNumber.length - 1; i >= 0; i -= 1) {
    // Turn the current character into a number
    let currentNum = parseInt(cleanedCardNumber[i], 10);

    // Every 2nd digit will double in value
    if (shouldDouble) {
      currentNum *= 2;
      // If the doubled digit is greater than 9, remove 9 from the number
      if (currentNum > 9) {
        currentNum -= 9;
      }
    }

    // Add the current number to the sum and reverse the doubling logic for the next number
    sum += currentNum;
    shouldDouble = !shouldDouble;
  }

  // Check to see if all the numbers added up is divisible by 10
  return sum % 10 === 0;
};

// Search the comment to see if there's any credit card numbers
export const containsCreditCardInfo = (comment: string) => {
  if (comment.length < 13) return false;

  // Check if there is a match for credit card numbers 13-21 digits long
  const cardNumberMatch = comment.match(/(?:\d[ -]?){13,21}\d/g);
  if (!cardNumberMatch) return false;

  return cardNumberMatch.some(luhnCheck);
};

const OneClickView: React.FC<OneClickViewProps> = observer(
  ({
    onLogout,
    onEditShipTo,
    onEditBillTo,
    onEditPayment,
    onScrollTopOfPage,
  }: OneClickViewProps) => {
    const rootStore = useMst();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { configuration, customer, cart, currentStep } = rootStore;

    const enabledPaymentMethods = useMemo(
      () => configuration.enabledPaymentMethods,
      [configuration.enabledPaymentMethods]
    );
    const [, setHasAddressError] = useState<boolean>(false);
    const [showGiftCardModal, setShowGiftCardModal] = useState<boolean>(false);
    const [clearGiftCard, setClearGiftCard] = useState<boolean>(false);
    const [formErrors, setFormErrors] = useState<string[]>([]);
    const [captchaResponse, setCaptchaResponse] = useState<string | null>(null);
    const [showMissingFields, setShowMissingFields] = useState(false);
    const [showWalletShipping, setShowWalletShipping] = useState(false);
    const [showWalletBilling, setShowWalletBilling] = useState(false);
    const [isAddressUpdating, setIsAddressUpdating] = useState(false);
    const [showSummarySections, setShowSummarySections] = useState(false);
    const [showPaymentOptions, setShowPaymentOptions] = useState(false);

    // Attempt to simplify state => UI mapping
    useEffect(() => {
      setShowMissingFields(
        rootStore.stateMatches('OneClick.Start.MissingFields')
      );
      setShowWalletShipping(
        rootStore.stateMatches('OneClick.WalletValidation.PartialShipping')
      );
      setShowWalletBilling(
        rootStore.stateMatches('OneClick.WalletValidation.PartialBilling')
      );
    }, [rootStore, currentStep]);

    const safeSetWalletFormError = () => {
      if (!formErrors.length) {
        const addressType = showWalletBilling ? 'billing' : 'shipping';
        setFormErrors([
          `The ${addressType} address provided was incomplete. Please review and fill in any missing information.`,
        ]);
      }
    };

    useEffect(() => {
      if (showWalletBilling || showWalletShipping) {
        safeSetWalletFormError();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showWalletBilling, showWalletShipping]);

    useEffect(() => {
      setShowSummarySections(
        !showMissingFields && !showWalletBilling && !showWalletShipping
      );
    }, [showMissingFields, showWalletBilling, showWalletShipping]);

    const changeCallback = async (response: string | null): Promise<void> => {
      if (configuration.reCaptcha.size === 'invisible') {
        return;
      }
      setCaptchaResponse(null);
      if (response) {
        const result = await configuration.verifyReCaptchaFlow(response);
        if (result === 200) {
          setCaptchaResponse(response);
          cart.updateSubmitToken(response);
        }
      }
    };
    const expiredCallback = () => setCaptchaResponse(null);
    const reCaptchaRef = useRef<ReCAPTCHA>(null);
    const applyButtonEnabledRef = useRef<boolean>(false);
    const countryOptions = configuration.countriesAsSelectOptions;

    const customFieldsSchema = yup.object().shape({
      customFields: getCustomFieldsSchemaWithFormContext(
        cart.cartData.customFields
      ),
    });

    const isUsingStripe = useMemo(
      () =>
        configuration.isEnabled(SiteLimitationKeys.STRIPE_SERVICE_ENABLED) &&
        enabledPaymentMethods.filter(method => {
          return method.methodType === 'VolusionPayments';
        }).length >= 1,
      [configuration, enabledPaymentMethods]
    );

    const cvvIsString: boolean = useMemo(() => {
      if (!cart.isPaymentRequired || customer.selectedPaymentProfileId <= 0) {
        return false;
      }
      const returnValue =
        !configuration.features.enableSpreedly && !isUsingStripe;
      return returnValue;
    }, [
      cart.isPaymentRequired,
      customer.selectedPaymentProfileId,
      configuration,
      isUsingStripe,
    ]);

    const cvvPaymentSchema = cvvIsString
      ? yup.object().shape({
          cvv: cvvSchema,
        })
      : yup.object().shape({
          cvv: yup.number().optional(),
        });

    const oneClickViewFormDefaultValues = {
      paymentMethodId: cart.cartData.paymentProfile.id
        ? cart.cartData.paymentProfile.id.toString()
        : configuration.defaultPaymentMethodId,
      billingAddressMethodId: cart.isBillingAddressSameAsShipping ? '0' : '1',
      billingAddress: {
        ...cart.cartData.billingAddress,
        country:
          cart.cartData.billingAddress.country ??
          DefaultValues.DEFAULT_SHIPPING_COUNTRY,
      },
      creditCard: {
        cardNumber: '',
        cardHolderName: '',
        expMonth: '',
        expYear: '',
        cvv: '',
      },
      stripe: '',
      cvv: '',
    };

    const oneClickViewFormSchema = yup
      .object()
      .concat(customFieldsSchema)
      .concat(cvvPaymentSchema);

    const oneClickViewForm = useForm({
      resolver: yupResolver(oneClickViewFormSchema),
      shouldUnregister: true,
      defaultValues: oneClickViewFormDefaultValues,
    });

    const handlePaymentMethodChanged = () => setFormErrors([]);

    const handleGetStateOptions = (
      country: string
    ): { options: SelectOption[]; label: string } => {
      const options = configuration.getCurrentStatesAsSelectOptions(country);
      const label = configuration.getStateLabel(country);
      return { options, label };
    };

    const handleShippingAddressSave = async (
      formData: AddressFormData
    ): Promise<void> => {
      setIsAddressUpdating(true);
      if (rootStore.stateMatches('OneClick.Start.MissingFields')) {
        await rootStore.updateAuthenticatedCustomerShippingAddress(
          formData as AddressContract
        );
        setIsAddressUpdating(false);
        return;
      }

      const { state: previousState, country: previousCountry } =
        cart.cartData.shippingAddress;
      try {
        const newAddress =
          await customer.addShippingAddressToAuthenticatedCustomer(
            formData as AddressContract
          );
        cart.updateShippingAddress(newAddress as AddressFormData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        cart.updateShippingAddress({ ...formData, id: -1, preferred: true });
      }
      if (showWalletShipping) {
        rootStore.send({ type: 'SHIPPING_ADDRESS_SAVED' });
      }
      if (
        cart.cartData.shippingAddress.state !== previousState ||
        cart.cartData.shippingAddress.country !== previousCountry
      ) {
        cart.setFetchingShippingMethods(true);
        await cart.updateCartFlow();
        setFormErrors([
          'Shipping Methods have been updated. Please confirm your selection.',
        ]);
        cart.setFetchingShippingMethods(false);
      } else {
        setFormErrors([]);
      }

      setIsAddressUpdating(false);
    };

    const handleBillingAddressSave = async (
      formData: AddressFormData
    ): Promise<void> => {
      setIsAddressUpdating(true);
      try {
        const newAddress =
          await customer.addBillingAddressToAuthenticatedCustomer(
            formData as AddressContract
          );
        cart.updateBillingAddress(newAddress);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        cart.updateBillingAddress(formData as AddressContract);
      }
      setIsAddressUpdating(false);
      setFormErrors([]);
      if (showWalletBilling) {
        rootStore.send({ type: 'BILLING_ADDRESS_SAVED' });
      }
    };

    const handleAddressUpdateCanceled = (): void => {
      rootStore.send({ type: 'EDIT_SHIPPING_CANCELED' });
    };

    const handleShippingMethodChanged = async (): Promise<void> => {
      setFormErrors([]);
      cart.setUpdatingCart(true);
      await rootStore.updateCartShippingAddressFlow();
      if (
        configuration.isEnabled(SiteLimitationKeys.STRIPE_SERVICE_ENABLED) &&
        configuration.hasStripePaymentMethodEnabled
      ) {
        await rootStore.putStripeIntentFlow();
      }
      cart.setUpdatingCart(false);
    };

    const handleApiValidationError = (error: ApiValidationError): void => {
      setFormErrors(error.extractErrorMessages());
      onScrollTopOfPage();
    };

    const handleGenericError = (error: ServiceError | Error): void => {
      setFormErrors([error.message]);
      onScrollTopOfPage();
    };

    const handleSpreedlyTransaction = (
      spreedlyTransaction: SpreedlyPostOrderResponse,
      orderId: string
    ) => {
      // Possible status values: 'succeeded', 'failed', 'pending', 'error'
      switch (spreedlyTransaction.status) {
        case null:
          // a null value for status is a success when not cc (we have an orderid at this point)
          rootStore.send({ type: 'ORDER_PLACED', orderId });
          break;
        case SpreedlyValues.SPREEDLY_3DS_SUCCEEDED:
          // Spreedly transaction succeed without needing 3DS authentication!
          rootStore.send({ type: 'ORDER_PLACED', orderId });
          break;
        case SpreedlyValues.SPREEDLY_3DS_PENDING:
          // user will have to authenticate
          SpreedlyService.start3DSLifecycle(
            spreedlyTransaction.transactionToken,
            configuration.payments.spreedlyEnvironmentKey
          );

          rootStore.send({ type: 'SPREEDLY_3DS_LIFECYCLE_STARTED' });
          break;
        default:
          // eslint-disable-next-line no-console
          console.log(`Event ${spreedlyTransaction.status} not handled`);
      }
    };

    const tryPlaceOrder = async (): Promise<void> => {
      setFormErrors([]);

      let reCaptchaStatus = 400;

      if (
        reCaptchaRef.current &&
        configuration.reCaptcha.checkoutEnabled &&
        configuration.reCaptcha.size === 'invisible'
      ) {
        cart.setUpdatingCart(false);
        cart.updateSubmitToken();
        const reCaptchaValue = await reCaptchaRef.current.executeAsync();
        if (reCaptchaValue) {
          cart.setUpdatingCart(true);
          reCaptchaStatus = await configuration.verifyReCaptchaFlow(
            reCaptchaValue
          );
        }
        reCaptchaRef.current.reset();
        if (reCaptchaStatus !== 200) {
          cart.setUpdatingCart(false);
          setFormErrors([UIConstants.RECAPTCHA_ERROR]);
          onScrollTopOfPage();
          return;
        }
        cart.updateSubmitToken(reCaptchaValue);
      }

      let orderId: string | undefined;
      let spreedlyTransaction;
      let requiresAction: boolean | undefined;

      cart.setUpdatingCart(true);

      try {
        if (configuration.features.enableSpreedly) {
          spreedlyTransaction = await cart.spreedlyPlaceOrder();
          orderId = spreedlyTransaction.orderId;
        } else {
          const orderResponse = await cart.placeOrder();
          orderId = orderResponse.orderId;
          requiresAction = orderResponse.requiresAction;
          if (requiresAction && orderResponse?.transactionToken) {
            cart.updateStripeInfo({
              clientSecretKey: orderResponse.transactionToken,
            });
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.isValidationError) {
          handleApiValidationError(error);
        } else {
          handleGenericError(error);
        }
      }
      if (orderId) {
        try {
          if (requiresAction === true) {
            const { error } = await rootStore.handleStripeNextAction(
              cart.stripeInfo.clientSecretKey
            );

            if (error) {
              handleGenericError(error);
              cart.setUpdatingCart(false);
              return;
            }
          }

          await customer.saveCustomerAddresses();

          const paymentProfileToSave = {
            ...cart.cartData.paymentProfile,
            id: customer.selectedPaymentProfileId ?? -1,
            preferred: true,
          } as CartPaymentProfileContract;

          await customer.saveNewPaymentProfile(paymentProfileToSave);

          if (spreedlyTransaction) {
            handleSpreedlyTransaction(spreedlyTransaction, orderId);
          } else {
            rootStore.send({ type: 'ORDER_PLACED', orderId });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      } else if (isUsingStripe) {
        cart.updateStripeInfo({ isWallet: false }); // clear isWallet and fetch a fresh intent to allow BNPLs
        await rootStore.postStripeIntentFlow();
      }
      cart.setUpdatingCart(false);
    };

    const handleUpdatingStripeIntent = async (): Promise<
      PaymentIntentResult | SetupIntentResult | undefined
    > => {
      if (
        !configuration.isEnabled(SiteLimitationKeys.STRIPE_SERVICE_ENABLED) ||
        !configuration.hasStripePaymentMethodEnabled ||
        cart.cartData.paymentProfile.method !== APIValues.STRIPE_METHOD
      ) {
        return undefined;
      }
      const stripeResponse = await rootStore.updateStripeIntentFlow();

      if (stripeResponse?.error) {
        return stripeResponse;
      }

      const intent = getIntentFromResponse(stripeResponse);
      if (!intent) {
        return undefined;
      }
      const paymentMethod = getPaymentMethodObject(intent);
      const intentCard = paymentMethod?.card;
      const expDateValues = getCardExpDateFromPaymentMethod(paymentMethod);
      cart.updateStripeInfo({
        clientSecretKey: cart.stripeInfo.clientSecretKey,
        paymentIntentId: getPaymentIntentIdFromResponse(stripeResponse),
        paymentMethodId: getPaymentMethodIdFromIntent(intent),
        cardBrand: getCardBrandFromPaymentMethod(paymentMethod),
        last4: intentCard?.last4 ?? null,
        setupIntentId: getSetupIntentIdFromResponse(stripeResponse),
        isSavedCard: false,
        isWallet: false,
        ...expDateValues,
      });

      cart.updatePaymentProfileWithStripeInfo();
      if (
        typeof intentCard?.exp_month !== 'undefined' &&
        typeof intentCard?.exp_year !== 'undefined'
      ) {
        cart.updateCardExpiration(intentCard.exp_month, intentCard?.exp_year);
      }

      return stripeResponse;
    };

    // Used when no saved payment method exists
    const tryPlaceOrderAlt = async (): Promise<void> => {
      setFormErrors([]);
      if (
        cart.hasRecurringBillingItem &&
        !cart.hasRecurringBillingPaymentMethod
      ) {
        setFormErrors([UIConstants.PROMPT_DIFFERENT_PAYMENT_METHOD]);
        onScrollTopOfPage();
        return;
      }

      let reCaptchaStatus = 400;

      if (
        reCaptchaRef.current &&
        configuration.reCaptcha.checkoutEnabled &&
        configuration.reCaptcha.size === 'invisible'
      ) {
        cart.setUpdatingCart(false);
        cart.updateSubmitToken();
        const reCaptchaValue = await reCaptchaRef.current.executeAsync();
        if (reCaptchaValue) {
          cart.setUpdatingCart(true);
          reCaptchaStatus = await configuration.verifyReCaptchaFlow(
            reCaptchaValue
          );
        }
        reCaptchaRef.current.reset();
        if (reCaptchaStatus !== 200) {
          cart.setUpdatingCart(false);
          setFormErrors([UIConstants.RECAPTCHA_ERROR]);
          onScrollTopOfPage();
          return;
        }
        cart.updateSubmitToken(reCaptchaValue);
      }

      let orderId: string | undefined;
      let spreedlyTransaction;
      let requiresAction: boolean | undefined;

      cart.setUpdatingCart(true);

      try {
        const stripeResponse = await handleUpdatingStripeIntent();

        if (stripeResponse?.error) {
          cart.setUpdatingCart(false);
          return;
        }

        if (configuration.features.enableSpreedly) {
          spreedlyTransaction = await cart.spreedlyPlaceOrder();
          orderId = spreedlyTransaction.orderId;
        } else {
          const orderResponse = await cart.placeOrder();
          orderId = orderResponse.orderId;
          requiresAction = orderResponse.requiresAction;
          if (requiresAction && orderResponse?.transactionToken) {
            cart.updateStripeInfo({
              clientSecretKey: orderResponse.transactionToken,
            });
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.isValidationError) {
          handleApiValidationError(error);
        } else {
          handleGenericError(error);
        }
      }

      if (orderId) {
        try {
          if (requiresAction === true) {
            const { error } = await rootStore.handleStripeNextAction(
              cart.stripeInfo.clientSecretKey
            );

            if (error) {
              handleGenericError(error);
              cart.setUpdatingCart(false);
              return;
            }
          }

          await customer.saveCustomerAddresses();

          const paymentProfileToSave = {
            ...cart.cartData.paymentProfile,
            id: customer.selectedPaymentProfileId ?? -1,
            preferred: true,
          } as CartPaymentProfileContract;

          await customer.saveNewPaymentProfile(paymentProfileToSave);

          if (spreedlyTransaction?.status) {
            handleSpreedlyTransaction(spreedlyTransaction, orderId);
          } else {
            rootStore.send({ type: 'ORDER_PLACED', orderId });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      } else if (isUsingStripe) {
        cart.updateStripeInfo({ isWallet: false }); // clear isWallet and fetch a fresh intent to allow BNPLs
        await rootStore.postStripeIntentFlow();
      }
      cart.setUpdatingCart(false);
    };

    type Theme = 'light' | 'dark';
    type Size = 'compact' | 'normal' | 'invisible';
    type Badge = 'bottomright' | 'bottomleft' | 'inline';

    const handleOnTokenRequest = async (
      creditCardData: OneClickFormData,
      cardTypeName: string
    ): Promise<string | null> => {
      const { cardNumber, cardHolderName, expMonth, expYear, cvv } =
        creditCardData.creditCard;
      cart.setIsTokenizingCard(true);
      const token = await cart.tokenizeCard({
        cardNumber: cardNumber?.replace(/-|\s/g, ''),
        cardHolderName,
        expireMonth: expMonth,
        expireYear: expYear,
        cvv,
        cardTypeName,
      });
      cart.setIsTokenizingCard(false);
      return token;
    };

    const validateCC = async (
      creditCardData: OneClickFormData
    ): Promise<void> => {
      const cardTypeName = UtilityService.getCreditCardType(
        creditCardData.creditCard.cardNumber
      );
      if (!cardTypeName) {
        oneClickViewForm.setError(
          'creditCard.cardNumber',
          {
            type: 'manual',
            message: UIConstants.CARD_NUMBER_INVALID_MESSAGE,
          },
          { shouldFocus: true }
        );
        throw new Error(UIConstants.CARD_NUMBER_INVALID_MESSAGE);
      }
      const matchingMethod = configuration.cardMethods.find(
        method => method.method.toLowerCase() === cardTypeName.toLowerCase()
      );
      if (!matchingMethod) {
        oneClickViewForm.setError(
          'creditCard.cardNumber',
          {
            type: 'manual',
            message: UIConstants.CARD_NOT_ACCEPTED_MESSAGE.replace(
              '{0}',
              cardTypeName
            ),
          },
          { shouldFocus: true }
        );
        throw new Error(UIConstants.CARD_NOT_ACCEPTED_MESSAGE);
      }

      const token = await handleOnTokenRequest(creditCardData, cardTypeName);

      if (!token) {
        oneClickViewForm.setError(
          'creditCard.cardNumber',
          {
            type: 'manual',
            message: UIConstants.TOKEN_FAILURE_MESSAGE,
          },
          { shouldFocus: true }
        );
        throw new Error(UIConstants.TOKEN_FAILURE_MESSAGE);
      }

      const { cardNumber, cardHolderName, expMonth, expYear, cvv } =
        creditCardData.creditCard;
      const last4 = cardNumber.slice(cardNumber.length - 4);
      const cardConfig: CartPaymentProfileCardContract = {
        cardBrand: null,
        pCIaaSId: token,
        cardNumber: `************${last4}`,
        last4,
        cardType: matchingMethod.id,
        cardTypeName,
        cvv: cvv ?? null,
        holdersName: cardHolderName,
        expMonth: `0${expMonth}`.slice(-2),
        expYear: `20${expYear}`.slice(-4),
        id: null,
        issueMonth: null,
        issueYear: null,
        issueNumber: null,
      };
      cart.updatePaymentProfile(matchingMethod, { cardConfig });
    };

    const validateAndUpdatePaymentProfile = async (
      oneClickFormData: OneClickFormData
    ): Promise<boolean> => {
      if (oneClickFormData.paymentMethodId === '-1') {
        try {
          await validateCC(oneClickFormData);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
          cart.setUpdatingCart(false);
          return false;
        }
      } else {
        cart.updatePaymentProfile(
          configuration.getMatchingPaymentMethod(
            oneClickFormData.paymentMethodId
          ),
          { poNumber: oneClickFormData?.purchaseOrder?.poNumber }
        );
      }
      return true;
    };

    // Used when no saved payment method exists
    const onPlaceOrder = async (oneClickFormData: OneClickFormData) => {
      setShowPaymentOptions(true);
      setShowGiftCardModal(false);
      setClearGiftCard(false);

      // Check to see if order comments is enabled, and if it contains credit card info
      if (
        configuration?.features?.enableOrderComments &&
        cart?.misc?.comment?.length
      ) {
        const creditCardFound = containsCreditCardInfo(cart.misc.comment);

        if (creditCardFound) {
          setFormErrors([
            'Please remove credit card information from the order comments.',
          ]);
          onScrollTopOfPage();
          return;
        }
      }

      if (applyButtonEnabledRef.current) {
        setShowGiftCardModal(true);
        return;
      }

      if (oneClickFormData.customFields) {
        Object.entries(oneClickFormData.customFields).forEach(
          ([key, value]) => {
            const fieldNameArr = key.split('_');
            const id = fieldNameArr[fieldNameArr.length - 1];
            cart.updateCustomField(Number(id), value);
          }
        );
      }

      if (oneClickFormData.paymentMethodId === '-1') {
        if (rootStore.payPalAcpOnSubmit) {
          await rootStore.payPalAcpOnSubmit();
          return;
        }
        if (configuration.features.enableSpreedly) {
          cart.setIsTokenizingCard(true);
          rootStore.validateSpreedlyInput();
          return;
        }
      }

      if (!oneClickFormData.paymentMethodId) {
        await tryPlaceOrderAlt();
      } else {
        const requiredAction = configuration.payments.getRequired3rdPartyAction(
          oneClickFormData.paymentMethodId
        );

        if (requiredAction && !cart.hasRecurringBillingItem) {
          setFormErrors([requiredAction]);
          onScrollTopOfPage();
          return;
        }

        const isPaymentValidated = await validateAndUpdatePaymentProfile(
          oneClickFormData
        );

        if (isPaymentValidated) {
          await tryPlaceOrderAlt();
        }
      }
    };

    const handlePlaceOrder = async (
      oneClickFormData: OneClickFormData
    ): Promise<void> => {
      if (
        cart.totals.grandTotal > 0 &&
        (cart.cartData.paymentProfile?.id === null ||
          cart.cartData.paymentProfile.id === -1)
      ) {
        setFormErrors([UIConstants.CHOOSE_PAYMENT_METHOD]);
        onScrollTopOfPage?.();
        return;
      }
      if (
        cart.hasRecurringBillingItem &&
        !cart.hasRecurringBillingPaymentMethod
      ) {
        setFormErrors([UIConstants.PROMPT_DIFFERENT_PAYMENT_METHOD]);
        onScrollTopOfPage?.();
        return;
      }

      // Check to see if order comments is enabled, and if it contains credit card info
      if (
        configuration?.features?.enableOrderComments &&
        cart?.misc?.comment?.length
      ) {
        const creditCardFound = containsCreditCardInfo(cart.misc.comment);

        if (creditCardFound) {
          setFormErrors([
            'Please remove credit card information from the order comments.',
          ]);
          onScrollTopOfPage();
          return;
        }
      }

      cart.setUpdatingCart(true);

      if (oneClickFormData.cvv) {
        cart.setIsTokenizingCard(true);
        try {
          await cart.tokenizeNewCVV(oneClickFormData.cvv);
          cart.setNeedsNewPciToken(false);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          oneClickViewForm.setError(
            'cvv',
            {
              type: 'manual',
              message: UIConstants.TOKEN_FAILURE_MESSAGE,
            },
            { shouldFocus: true }
          );
          cart.setIsTokenizingCard(false);
          cart.setUpdatingCart(false);
          return;
        }
        cart.setIsTokenizingCard(false);
      }

      if (oneClickFormData.customFields) {
        Object.entries(oneClickFormData.customFields).forEach(
          ([key, value]) => {
            const fieldNameArr = key.split('_');
            const id = fieldNameArr[fieldNameArr.length - 1];
            cart.updateCustomField(Number(id), value);
          }
        );
      }

      if (rootStore.invalidCountryShippingAddress) {
        setFormErrors([UIConstants.INVALID_COUNTRY_ERROR]);
        cart.setUpdatingCart(false);
        onScrollTopOfPage();
        return;
      }

      if (rootStore.invalidStateShippingAddress) {
        setFormErrors([UIConstants.INVALID_STATE_ERROR]);
        cart.setUpdatingCart(false);
        onScrollTopOfPage();
        return;
      }

      if (!rootStore.cart.hasBillingAddress) {
        setFormErrors([UIConstants.EMPTY_BILLING_ADDRESS]);
        cart.setUpdatingCart(false);
        onScrollTopOfPage();
        return;
      }

      if (
        configuration.features.enableSpreedly &&
        cart.isPaymentRequired &&
        customer.selectedPaymentProfileId > 0
      ) {
        rootStore.recacheSpreedlyCard();
        return;
      }

      await tryPlaceOrder();
    };

    const paymentSectionPosition = rootStore.stateMatches(
      'OneClick.Start.MissingFields'
    )
      ? 'First'
      : 'Between';

    const renderAddressForm = (): ReactElement => {
      const shippingAddress = cart.shippingAddress.country
        ? (cart.shippingAddress as AddressFormData)
        : { country: configuration.defaultCountry, residential: true };
      return customer.shippingAddressCount > 0 ? (
        <AddressFormWithButtons
          defaultValues={shippingAddress}
          isShippingAddress
          countryOptions={countryOptions}
          getStateConfig={handleGetStateOptions}
          onSubmitClicked={handleShippingAddressSave}
          onErrorStatus={setHasAddressError}
          missingFields={cart.missingRequiredFields}
          makeNonMissingFieldsEditable
          autoFocus
          displayCancelButton
          cancelButtonText="Select another address"
          onCancelButtonClicked={handleAddressUpdateCanceled}
          shippingRatesAddressType={
            configuration.features.shippingRatesAddressType
          }
          isSaving={isAddressUpdating}
        />
      ) : (
        <AddressFormWithButtons
          defaultValues={shippingAddress}
          isSaving={isAddressUpdating}
          isShippingAddress
          countryOptions={countryOptions}
          getStateConfig={handleGetStateOptions}
          onSubmitClicked={handleShippingAddressSave}
          onErrorStatus={setHasAddressError}
          missingFields={cart.missingRequiredFields}
          makeNonMissingFieldsEditable
          autoFocus
          shippingRatesAddressType={
            configuration.features.shippingRatesAddressType
          }
        />
      );
    };

    if (!cart.shippingAddress.id && !cart.shippingAddress.address1) {
      rootStore.send({ type: 'ADD_SHIPPING_ADDRESS' });
    }
    if (!cart.billingAddress.id && !cart.isBillingAddressSameAsShipping) {
      rootStore.send({ type: 'ADD_BILLING_ADDRESS' });
    }

    const handleSpreedlyOnRecache = async (
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      token: string,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      paymentMethod: spreedly.SpreedlyPaymentMethod
    ): Promise<void> => {
      await tryPlaceOrder();
    };

    const handleSpreedlyOnErrors = (errors: spreedly.SpreedlyError[]): void => {
      setFormErrors([]);
      if (errors.length) {
        const errorList = errors.map(error => error.message);
        setFormErrors([UIConstants.TOKEN_FAILURE_MESSAGE, ...errorList]);
        cart.setIsTokenizingCard(false);
        cart.setUpdatingCart(false);
        onScrollTopOfPage();
      }
    };

    const handleSpreedlyOnThreeDSStatus = async (
      event: SpreedlyThreeDSStatusEvent
    ) => {
      // Possible event action values: 'succeeded', 'challenge', 'finalization-timeout', 'error'
      switch (event.action) {
        case SpreedlyValues.SPREEDLY_3DS_CHALLENGE:
          // show the challenge-modal
          cart.setShowSpreedlyChallengeModal(true);
          break;
        case SpreedlyValues.SPREEDLY_3DS_SUCCEEDED:
          // finish checkout and redirect to success page
          cart.setShowSpreedlyChallengeModal(false);

          cart.updateSpreedlyTransactionDetails({
            transactionToken: event.token,
            challengeResponse: event.action,
          });

          await tryPlaceOrder();
          break;
        default:
          rootStore.send({ type: 'ORDER_ERROR' });
      }
    };

    const setApplyButtonEnabled = (enabled: boolean) => {
      applyButtonEnabledRef.current = enabled;
    };

    const handleGiftCardModalContinueButton = () => {
      setClearGiftCard(true);
      oneClickViewForm.handleSubmit(onPlaceOrder)();
    };

    const handleSpreedlyOnValidation = (
      inputProperties: spreedly.SpreedlyFieldEventInputProperties
    ) => {
      if (inputProperties.validCvv && inputProperties.validNumber) {
        const formValues = oneClickViewForm.getValues();
        rootStore.tokenizeSpreedlyCard({
          cardHolderName: formValues.creditCard.cardHolderName,
          expMonth: formValues.creditCard.expMonth,
          expYear: formValues.creditCard.expYear,
        });
        return;
      }
      cart.setIsTokenizingCard(false);
    };

    const handleSpreedlyOnPaymentMethod = async (
      token: string,
      paymentMethod: spreedly.SpreedlyPaymentMethod
    ): Promise<void> => {
      const convertedCardType = SpreedlyService.convertSpreedlyToV1CardType(
        paymentMethod.card_type
      );
      const matchingMethod = configuration.cardMethods.find(
        method =>
          method.method.toLowerCase() === convertedCardType.toLowerCase()
      );
      if (!matchingMethod) {
        setFormErrors([
          UIConstants.CARD_NOT_ACCEPTED_MESSAGE.replace(
            '{0}',
            convertedCardType
          ),
        ]);
        onScrollTopOfPage();
        cart.setIsTokenizingCard(false);
        return;
      }
      const formValues = oneClickViewForm.getValues();
      const last4 = paymentMethod.last_four_digits;
      const cardConfig: CartPaymentProfileCardContract = {
        cardBrand: null,
        pCIaaSId: null,
        spreedlyPaymentToken: token,
        paymentMethodFingerprint: paymentMethod.fingerprint,
        cardNumber: `************${last4}`,
        last4,
        cardType: matchingMethod.id,
        cardTypeName: matchingMethod.method,
        cvv: null,
        holdersName: paymentMethod.full_name,
        expMonth:
          paymentMethod.month?.toString()?.padStart(2, '0') ??
          formValues.creditCard.expMonth,
        expYear:
          paymentMethod.year?.toString() ?? formValues.creditCard.expYear,
        id: null,
        issueMonth: null,
        issueYear: null,
        issueNumber: null,
        browserInfo:
          configuration.payments.isSpreedly3DSEnabled &&
          configuration.features.enableSpreedly
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              Spreedly.ThreeDS.serialize('03', configuration.headerAccept)
            : null,
        transactionToken: null,
        challengeResponse: null,
      };
      cart.updatePaymentProfile(matchingMethod, { cardConfig });

      cart.setIsTokenizingCard(false);
      await tryPlaceOrderAlt();
    };

    const spreedlyEventHandlers = {
      onRecache: handleSpreedlyOnRecache,
      onErrors: handleSpreedlyOnErrors,
      onThreeDSStatus: handleSpreedlyOnThreeDSStatus,
    };

    const spreedlyEventHandlersAlt = configuration.features.enableSpreedly
      ? {
          onValidation: handleSpreedlyOnValidation,
          onPaymentMethod: handleSpreedlyOnPaymentMethod,
          onErrors: handleSpreedlyOnErrors,
          onThreeDSStatus: handleSpreedlyOnThreeDSStatus,
        }
      : undefined;

    const amazonPayProps = {
      clientId: configuration.amazonPay.clientId,
      sellerId: configuration.amazonPay.sellerId,
      isSandbox: configuration.amazonPay.isSandbox,
      isLoaded: rootStore.isAmazonPayLoaded,
      onLoaded: (): void => rootStore.setAmazonLoaded(true),
      onAuthorize: async (accessToken: string): Promise<void> => {
        rootStore.send({
          type: 'AMAZONPAY_ON_AUTHORIZE',
          amazonPayToken: accessToken,
        });
      },
    };

    const payPalButtonMethods = {
      createOrder: rootStore.createPayPalOrder,
      onApprove: async (data: PayPalOnApproveData): Promise<void> => {
        cart.paypalInfo.setIsVenmo(cart.paypalInfo.isVenmoPending);
        rootStore.send({ type: 'PAYPAL_ON_APPROVED', paypalParams: data });
      },
      onClick: async (data: PayPalOnClickData): Promise<void> => {
        cart.paypalInfo.setIsVenmoPending(
          data.fundingSource?.toLowerCase() === 'venmo'
        );
      },
    };

    const handlePayPalAcpOnCardValidated = async (
      data: PayPalAcpHostedFieldsResponseData
    ): Promise<void> => {
      rootStore.setPayPalAcpDetails(data);
      await tryPlaceOrderAlt();
    };

    const acpSetupMethods: PayPalAcpMethods | undefined =
      cart.isUsingPayPalAcp &&
      configuration.paypalExpress.showCreditDebitCardLogos
        ? {
            onCardValidated: handlePayPalAcpOnCardValidated,
          }
        : undefined;

    const renderCvvInput = (): React.ReactNode => {
      if (cart.paymentProfile.method === 'VolusionPayments') {
        return null;
      }

      if (configuration.features.enableSpreedly) {
        return (
          <SpreedlyCvvOnly spreedlyEventHandlers={spreedlyEventHandlers} />
        );
      }
      return <CVVUpdateSectionWithFormContext />;
    };

    return (
      <>
        {!!configuration.messages.oneClick && (
          <CustomMessage customMessage={configuration.messages.oneClick} />
        )}
        <FormErrorLabel errors={formErrors} />

        {showSummarySections && (
          <AuthenticatedContactSection
            emailAddress={customer.email as string}
            onLogout={onLogout}
            groupPosition="First"
          />
        )}

        {showMissingFields && (
          <div className="container px-2 flex flex-col pb-4">
            <SectionTitle>Shipping Address</SectionTitle>
            {renderAddressForm()}
          </div>
        )}
        {showWalletShipping && (
          <AddressFormWithButtons
            defaultValues={cart.cartData.shippingAddress as AddressFormData}
            isShippingAddress
            isSaving={isAddressUpdating}
            countryOptions={countryOptions}
            getStateConfig={handleGetStateOptions}
            onErrorStatus={setHasAddressError}
            onSubmitClicked={handleShippingAddressSave}
            makeNonMissingFieldsEditable
            shippingRatesAddressType={
              configuration.features.shippingRatesAddressType
            }
          />
        )}
        {showSummarySections && (
          <AuthenticatedShippingAddressSection
            shippingAddress={
              cart.shippingAddress as CustomerShippingAddressContract
            }
            onShipToInfoEdit={onEditShipTo}
            groupPosition="Between"
            toolTipKey="OneClickShippingAddressKey"
          />
        )}
        {showSummarySections &&
          (cart.isPaymentRequired &&
          cart.cartData.paymentProfile.id &&
          !showPaymentOptions ? (
            <AuthenticatedPaymentSection
              paymentProfile={cart.cartData.paymentProfile}
              onPaymentEdit={onEditPayment}
              groupPosition={paymentSectionPosition}
              appliedGiftCards={cart.cartData.giftCards}
            />
          ) : showSummarySections && !cart.isPaymentRequired ? (
            <NoPaymentSummary
              groupPosition="Between"
              appliedGiftCards={cart.cartData.giftCards}
              onPaymentEdit={onEditPayment}
            />
          ) : null)}
        {showSummarySections &&
          (configuration.features.requireBillToSameAsShipTo ? (
            <AuthenticatedBillingAddressSection
              displaySameAsShipping={cart.isBillingAddressSameAsShipping}
              billingAddress={
                cart.billingAddress as CustomerBillingAddressContract
              }
              disallowEdit
              groupPosition="Last"
            />
          ) : (
            <AuthenticatedBillingAddressSection
              displaySameAsShipping={cart.isBillingAddressSameAsShipping}
              billingAddress={
                cart.billingAddress as CustomerBillingAddressContract
              }
              onBillToInfoEdit={onEditBillTo}
              groupPosition="Last"
            />
          ))}
        {showWalletBilling && (
          <AddressFormWithButtons
            defaultValues={cart.cartData.billingAddress as AddressFormData}
            isSaving={isAddressUpdating}
            countryOptions={countryOptions}
            getStateConfig={handleGetStateOptions}
            onErrorStatus={setHasAddressError}
            onSubmitClicked={handleBillingAddressSave}
            makeNonMissingFieldsEditable
          />
        )}

        {/* Invisible Stripe Loader Section */}
        {configuration.hasStripePaymentMethodEnabled && (
          <StripeInvisibleLoader />
        )}

        {showSummarySections && (
          <FormProvider {...oneClickViewForm}>
            <DevTool control={oneClickViewForm.control} placement="top-right" />
            <form
              onSubmit={
                cart.isPaymentRequired &&
                cart.cartData.paymentProfile.id &&
                !showPaymentOptions
                  ? oneClickViewForm.handleSubmit(handlePlaceOrder)
                  : oneClickViewForm.handleSubmit(onPlaceOrder)
              }
            >
              {showGiftCardModal && (
                <AlertModal
                  handleContinueButton={handleGiftCardModalContinueButton}
                  headerText="Gift Card Not Applied"
                  bodyText="You’ve entered a gift card without applying it. Are you
                  sure you want to continue?"
                />
              )}
              {cart.isPaymentRequired &&
                customer.selectedPaymentProfileId > 0 &&
                renderCvvInput()}
              <ShippingMethodSection
                onValidData={handleShippingMethodChanged}
              />
              {cart.isPaymentRequired &&
              (!cart.cartData.paymentProfile.id || showPaymentOptions) ? (
                <PaymentMethodSectionWithFormContext
                  onPaymentMethodChanged={handlePaymentMethodChanged}
                  hasRecurringBillingItem={cart.hasRecurringBillingItem}
                  amazonPayButtonProps={amazonPayProps}
                  customPayMessage={configuration.messages.pay}
                  paymentMethods={
                    configuration.enabledPaymentMethodsWithFormContext
                  }
                  payPalButtonMethods={payPalButtonMethods}
                  payPalAcpMethods={acpSetupMethods}
                  spreedlyEventHandlers={spreedlyEventHandlersAlt}
                />
              ) : null}

              {configuration.features.enableGiftCards && (
                <GiftCardSection
                  onGiftCardsUpdating={() => setFormErrors([])}
                  setApplyButtonEnabled={setApplyButtonEnabled}
                  clearGiftCard={clearGiftCard}
                />
              )}

              <CustomFieldSection
                customFields={cart.cartData.customFields}
                useContext
              />
              {configuration.features.enableSpreedly && (
                <SpreedlyChallengeModal
                  showModal={cart.showSpreedlyChallengeModal}
                />
              )}
              {configuration?.features?.enableOrderComments && (
                <OrderComment
                  orderComment={cart.cartData.misc?.comment || ''}
                  onOrderCommentUpdated={cart.updateComment}
                />
              )}
              {configuration?.reCaptcha?.checkoutEnabled && (
                <div className="flex items-center justify-center">
                  <ReCAPTCHA
                    ref={reCaptchaRef}
                    sitekey={configuration?.reCaptcha?.siteKey as string}
                    size={configuration?.reCaptcha?.size as Size}
                    hl={configuration?.reCaptcha?.hl as string}
                    theme={configuration?.reCaptcha?.theme as Theme}
                    badge={configuration?.reCaptcha?.badge as Badge}
                    onExpired={expiredCallback}
                    onChange={changeCallback}
                  />
                </div>
              )}
              <Button
                center
                className="max-w-sm"
                isDisabled={
                  configuration?.reCaptcha?.checkoutEnabled &&
                  configuration?.reCaptcha?.size !== 'invisible' &&
                  !captchaResponse
                }
                isOtherActionPending={
                  cart.isTokenizingCard ||
                  cart.updatingCart ||
                  rootStore.stateMatches('OneClick.Start.ThreeDSPending')
                }
              >
                {cart.stripeInfo.isWallet
                  ? 'Complete Order'
                  : `Pay ${
                      configuration.formattedCurrencySymbol
                    }${cart.grandTotal.toFixed(2)}`}
              </Button>
            </form>
          </FormProvider>
        )}
      </>
    );
  }
);

export default OneClickView;
